import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { Button, Card, Description, Input, Label, TextField, ToggleButton, ToggleButtonGroup } from '@heroui/react'
import { Messages } from '../admin/parts'
import { useTextLanguages } from '../lib/i18n/useCustomerLanguages'
import { MediaPlayer } from '../layout/MediaPlayer'
import { objectFitOf } from '../layout/playlist'
import { useCanvasScale } from '../layout/render'
import type { LayoutElement, MediaFit } from '../layout/types'
import { api, type PlaylistEntry, type PlaylistSummary, type SignageMedia, type TickerLine } from '../lib/api'
import { accepts } from '../lib/filePicker'
import { FOCUSABLE, trapStep } from '../lib/focusTrap'
import {
  addEntries,
  afterRemove,
  centreSize,
  moveByKey,
  playItems,
  reducedMotion,
  invalidItems,
  shownTotal,
  secondsValid,
  stageCanvas,
  stripSeconds,
  usageOf,
  toSend,
  typedEntry,
  type DraftEntry,
  type Said,
  type StageShape,
} from '../lib/playlistView'
import {
  LIBRARY_ACCEPT,
  MAX_ENTRIES,
  MAX_SECONDS,
  MAX_TICKER_LINES,
  MIN_SECONDS,
  clockOf,
  filmLength,
  moveEntry,
  nonBlankTicker,
  playlistProblems,
} from '../lib/signageEdit'
import { explain } from '../lib/staffErrors'
import type { StaffKey } from '../lib/staffText'
import { uploadEach, type UploadRow } from '../lib/uploadQueue'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { Empty, PickOne, SignageModal, UploadButton } from './signageKit'
import { filmHover, Thumb, UploadLine, uploadToLibrary } from './signageLibrary'

type Draft = { id?: string; name: string; version: number; entries: DraftEntry[]; ticker: TickerLine[] }

const FITS: ReadonlyArray<[MediaFit, StaffKey]> = [
  ['fill', 'signage.fit.fill'],
  ['fit', 'signage.fit.fit'],
  ['centre', 'signage.fit.centre'],
]

const SHAPES: ReadonlyArray<[StageShape, string]> = [
  ['wide', '16:9'],
  ['tall', '9:16'],
]

/** Saved playlists: the cards, and one being edited. */
export function PlaylistsTab() {
  const { say, lang } = useStaffText()
  const [lists, setLists] = useState<PlaylistSummary[]>([])
  const [library, setLibrary] = useState<SignageMedia[]>([])
  const [draft, setDraft] = useState<Draft | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [notice, setNotice] = useState('')
  const [done, setDone] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const [p, m] = await Promise.all([api.playlists(), api.signageMedia()])
    setLists(p.playlists)
    setLibrary(m.media)
  }, [])
  useEffect(() => {
    load()
      .catch((err) => setNotice(explain(err, lang, 'common.unreachable')))
      .finally(() => setLoaded(true))
  }, [load, lang])

  const open = async (id?: string) => {
    setNotice('')
    setDone('')
    if (!id) {
      setDraft({ name: '', version: 0, entries: [], ticker: [] })
      return
    }
    try {
      const { playlist } = await api.playlist(id)
      setDraft({ id: playlist.id, name: playlist.name, version: playlist.version, entries: playlist.entries, ticker: playlist.ticker })
    } catch (err) {
      setNotice(explain(err, lang, 'common.unreachable'))
    }
  }

  const run = async (fn: () => Promise<unknown>, doneKey: StaffKey = 'common.saved') => {
    setBusy(true)
    setNotice('')
    setDone('')
    try {
      await fn()
      setDraft(null)
      await load()
      setDone(say(doneKey))
    } catch (err) {
      setNotice(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  const save = () => {
    if (!draft) return
    const problems = playlistProblems(draft.name, draft.entries, draft.ticker, library)
    if (problems.length > 0) {
      setNotice(problems.map((k) => say(k)).join(' '))
      return
    }
    const body = { name: draft.name.trim(), entries: toSend(draft.entries), ticker: nonBlankTicker(draft.ticker) }
    void run(() => (draft.id ? api.savePlaylist(draft.id, { ...body, version: draft.version }) : api.createPlaylist(body)))
  }

  const newButton = (
    <Button variant="primary" size="sm" onPress={() => void open()}>
      <Icon name="plus" size={16} /> {say('signage.newPlaylist')}
    </Button>
  )

  return (
    <Card className="signage-panel">
      <Messages error={notice} notice={done} />
      {draft ? (
        <PlaylistEditor
          draft={draft}
          library={library}
          busy={busy}
          onChange={(update) => setDraft((d) => (d ? update(d) : d))}
          onUploaded={(m) => setLibrary((l) => [m, ...l.filter((x) => x.id !== m.id)])}
          onSave={save}
          onCancel={() => {
            setNotice('')
            setDraft(null)
          }}
          onDelete={draft.id ? () => void run(() => api.deletePlaylist(draft.id!), 'signage.deleted') : undefined}
        />
      ) : (
        <>
          {lists.length > 0 && (
            <div className="signage-toolbar">
              <span className="signage-count">{say('signage.nPlaylists', { n: lists.length })}</span>
              {newButton}
            </div>
          )}
          {!loaded ? (
            <p className="signage-loading">{say('common.loading')}</p>
          ) : lists.length === 0 ? (
            <Empty icon="play" title={say('signage.playlists.empty')} action={newButton} />
          ) : (
            <ul className="signage-cards">
              {lists.map((p) => (
                <li key={p.id}>
                  <PlaylistCard playlist={p} onOpen={() => void open(p.id)} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  )
}

function useSaid() {
  const { say } = useStaffText()
  return (s: Said) => say(s.key, s.n === undefined ? undefined : { n: s.n })
}

/** A phrase, or a refused time as it was typed. */
function useShown() {
  const said = useSaid()
  return (s: Said | { raw: string }) => ('raw' in s ? s.raw : said(s))
}

function PlaylistCard({ playlist: p, onOpen }: { playlist: PlaylistSummary; onOpen: () => void }) {
  const { say } = useStaffText()
  const said = useSaid()
  const cover = p.cover
  const usedBy = [...p.campaigns, ...p.designs].join(', ')
  return (
    <Card
      role="button"
      tabIndex={0}
      className="signage-card"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
      {...filmHover}
    >
      <span className="signage-card__cover">
        {cover ? <Thumb media={cover} className="signage-face" /> : <Icon name="play" size={28} />}
      </span>
      <Card.Content className="signage-card__body">
        <Card.Title className="signage-card__name">{p.name}</Card.Title>
        <span className="signage-card__meta">
          {say('signage.nItems', { n: p.items })} · {clockOf(p.totalSeconds)}
        </span>
        <span className="signage-card__used" title={usedBy || undefined}>
          {usageOf(p).map(said).join(' · ')}
        </span>
      </Card.Content>
    </Card>
  )
}

function PlaylistEditor({
  draft,
  library,
  busy,
  onChange,
  onUploaded,
  onSave,
  onCancel,
  onDelete,
}: {
  draft: Draft
  library: SignageMedia[]
  busy: boolean
  onChange: (update: (d: Draft) => Draft) => void
  onUploaded: (m: SignageMedia) => void
  onSave: () => void
  onCancel: () => void
  onDelete?: () => void
}) {
  const { say } = useStaffText()
  // The ticker stores az, ru and en only (signage.TickerLine); the others wait for its own migration.
  const offeredTicker = useTextLanguages().filter(([c]) => c === 'az' || c === 'ru' || c === 'en')
  const TEXT_LANGUAGES: Array<[string, string]> = offeredTicker.length > 0 ? offeredTicker : [['en', 'English']]
  const shown = useShown()
  const [asking, setAsking] = useState(false)
  const [selected, setSelected] = useState(draft.entries.length > 0 ? 0 : -1)
  const [shape, setShape] = useState<StageShape>('wide')
  const [picking, setPicking] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [dragging, setDragging] = useState<number | null>(null)
  const strip = useRef<Array<HTMLButtonElement | null>>([])
  const refocus = useRef(false)
  const byId = new Map(library.map((m) => [m.id, m]))
  const set = (patch: Partial<Draft>) => onChange((d) => ({ ...d, ...patch }))
  const setEntry = (i: number, e: DraftEntry) => set({ entries: draft.entries.map((x, j) => (j === i ? e : x)) })
  const setLine = (i: number, line: TickerLine) => set({ ticker: draft.ticker.map((l, j) => (j === i ? line : l)) })
  const entry = draft.entries[selected]
  const media = entry ? byId.get(entry.mediaItemId) : undefined
  const bad = invalidItems(draft.entries, library)
  const total = shownTotal(draft.entries, library)

  // An item moved by the keyboard keeps the focus as it moves.
  useEffect(() => {
    if (!refocus.current) return
    refocus.current = false
    strip.current[selected]?.focus()
  }, [selected, draft.entries])

  const remove = (i: number) => {
    setSelected(afterRemove(draft.entries.length, i, selected))
    set({ entries: draft.entries.filter((_, j) => j !== i) })
  }
  const move = (from: number, to: number) => {
    set({ entries: moveEntry([...draft.entries], from, to) })
    setSelected(to)
  }

  return (
    <div className="signage-ed">
      <div className="signage-ed__head">
        <Button isIconOnly variant="ghost" size="sm" aria-label={say('common.back')} onPress={onCancel}>
          <Icon name="left" size={16} />
        </Button>
        <Input
          variant="secondary"
          className="signage-ed__name"
          aria-label={say('signage.name')}
          placeholder={say('signage.name')}
          value={draft.name}
          maxLength={80}
          onChange={(e) => set({ name: e.target.value })}
        />
        <span className="signage-ed__meta">
          {say('signage.nItems', { n: draft.entries.length })} · {total === null ? <span className="signage-ed__nototal">—</span> : clockOf(total)}
        </span>
        <span className="signage-ed__acts">
          <Button variant="secondary" size="sm" isDisabled={playItems(draft.entries, library).length === 0} onPress={() => setPlaying(true)}>
            <Icon name="play" size={14} /> {say('signage.play')}
          </Button>
          <Button variant="primary" size="sm" isDisabled={busy || bad.length > 0} onPress={onSave}>
            {say('common.save')}
          </Button>
        </span>
      </div>

      <div className="signage-ed__body">
        <div className="signage-ed__main">
          <ToggleButtonGroup
            className="signage-shape"
            size="sm"
            selectionMode="single"
            disallowEmptySelection
            aria-label={say('signage.shape')}
            selectedKeys={[shape]}
            onSelectionChange={(keys) => {
              const next = [...keys][0]
              if (next === 'wide' || next === 'tall') setShape(next)
            }}
          >
            {SHAPES.map(([value, label]) => (
              <ToggleButton key={value} id={value}>
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Stage entry={entry} media={media} shape={shape} hold={playing} onAdd={draft.entries.length < MAX_ENTRIES ? () => setPicking(true) : undefined} />
        </div>
        <aside className="signage-ed__panel" aria-label={say('signage.item')}>
          <span className="signage-label">{say('signage.item')}</span>
          {entry ? (
            <>
              <span className="signage-ed__file">{media?.name ?? entry.mediaItemId}</span>
              <SecondsField key={`${selected}:${entry.mediaItemId}`} entry={entry} media={media} onChange={(next) => setEntry(selected, next)} />
              <PickOne
                label={say('signage.fit')}
                value={entry.fit}
                onChange={(fit) => setEntry(selected, { ...entry, fit: fit as MediaFit })}
                options={FITS.map(([value, key]) => ({ value, label: say(key) }))}
              />
              <Button variant="ghost" size="sm" className="signage-ed__remove" onPress={() => remove(selected)}>
                <Icon name="trash" size={14} /> {say('common.remove')}
              </Button>
            </>
          ) : (
            <span className="signage-ed__none">—</span>
          )}
        </aside>
      </div>

      <ol className="signage-strip" aria-label={say('signage.items')}>
        {draft.entries.map((e, i) => {
          const m = byId.get(e.mediaItemId)
          return (
            <li
              key={`${i}:${e.mediaItemId}`}
              className="signage-strip__cell"
              data-dragging={dragging === i || undefined}
              onDragOver={(ev) => ev.preventDefault()}
              onDrop={() => {
                if (dragging !== null && dragging !== i) move(dragging, i)
                setDragging(null)
              }}
            >
              <button
                type="button"
                ref={(el) => {
                  strip.current[i] = el
                }}
                className="signage-strip__item"
                aria-pressed={selected === i}
                aria-invalid={bad.includes(i) || undefined}
                aria-label={`${i + 1}. ${m?.name ?? e.mediaItemId}`}
                draggable
                onDragStart={() => setDragging(i)}
                onDragEnd={() => setDragging(null)}
                onClick={() => setSelected(i)}
                onKeyDown={(ev) => {
                  const next = moveByKey(draft.entries, i, ev.key)
                  if (!next) return
                  ev.preventDefault()
                  refocus.current = true
                  set({ entries: next.entries })
                  setSelected(next.index)
                }}
              >
                <span className="signage-strip__face">
                  {m ? <Thumb media={m} className="signage-face" /> : <Icon name="alert" size={16} />}
                  {m && filmLength(m) && <span className="signage-strip__badge">{filmLength(m)}</span>}
                </span>
                <span className="signage-strip__foot">
                  <span>{i + 1}</span>
                  <span>{shown(stripSeconds(e, m))}</span>
                </span>
              </button>
              <Button isIconOnly size="sm" className="signage-strip__x" aria-label={say('common.remove')} onPress={() => remove(i)}>
                <Icon name="close" size={12} />
              </Button>
            </li>
          )
        })}
        {draft.entries.length < MAX_ENTRIES && (
          <li className="signage-strip__cell">
            <button type="button" className="signage-strip__add" onClick={() => setPicking(true)}>
              <Icon name="plus" size={18} />
              <span>{say('common.add')}</span>
            </button>
          </li>
        )}
      </ol>

      <section className="signage-ed__ticker" aria-label={say('signage.ticker')}>
        <span className="signage-label">{say('signage.ticker')}</span>
        {draft.ticker.length > 0 && TEXT_LANGUAGES.length > 1 && (
          <div className="signage-ticker signage-ticker--head" aria-hidden="true" style={{ ['--ticker-langs' as string]: TEXT_LANGUAGES.length }}>
            {TEXT_LANGUAGES.map(([code, label]) => (
              <span key={code}>{label}</span>
            ))}
            <span />
          </div>
        )}
        {draft.ticker.map((line, i) => (
          <div key={i} className="signage-ticker" style={{ ['--ticker-langs' as string]: TEXT_LANGUAGES.length }}>
            {TEXT_LANGUAGES.map(([code, label]) => (
              <Input
                key={code}
                variant="secondary"
                aria-label={label}
                placeholder={code}
                maxLength={200}
                value={line[code as keyof TickerLine]}
                onChange={(ev) => setLine(i, { ...line, [code]: ev.target.value })}
              />
            ))}
            <Button isIconOnly variant="ghost" size="sm" aria-label={say('common.remove')} onPress={() => set({ ticker: draft.ticker.filter((_, j) => j !== i) })}>
              <Icon name="trash" size={14} />
            </Button>
          </div>
        ))}
        {draft.ticker.length < MAX_TICKER_LINES && (
          <Button variant="ghost" size="sm" className="signage-ed__line" onPress={() => set({ ticker: [...draft.ticker, { az: '', ru: '', en: '' }] })}>
            <Icon name="plus" size={14} /> {say('signage.addLine')}
          </Button>
        )}
      </section>

      {onDelete && (
        <div className="signage-ed__foot">
          <Button
            variant={asking ? 'danger' : 'danger-soft'}
            size="sm"
            isDisabled={busy}
            onPress={() => {
              setAsking(!asking)
              if (asking) onDelete()
            }}
          >
            {say(asking ? 'signage.deleteAsk' : 'common.delete')}
          </Button>
        </div>
      )}

      {picking && (
        <PickFiles
          library={library}
          room={MAX_ENTRIES - draft.entries.length}
          onUploaded={onUploaded}
          onClose={() => setPicking(false)}
          onPick={(chosen) => {
            onChange((d) => ({ ...d, entries: addEntries(d.entries, chosen) }))
            if (selected < 0 && chosen.length > 0) setSelected(0)
            setPicking(false)
          }}
        />
      )}
      {playing && <PlayAll entries={draft.entries} library={library} shape={shape} onClose={() => setPlaying(false)} />}
    </div>
  )
}

/** A picture's seconds, or a film's: to its end or a number. Same limits as the server. */
function SecondsField({ entry, media, onChange }: { entry: DraftEntry; media?: SignageMedia; onChange: (next: DraftEntry) => void }) {
  const { say } = useStaffText()
  const kind = media?.kind ?? 'image'
  const [text, setText] = useState(entry.typed ?? (entry.seconds > 0 ? String(entry.seconds) : ''))
  const { typed: _, ...kept } = entry
  const toEnd = kind === 'video' && entry.seconds === 0
  const bad = !secondsValid(entry.seconds, kind)
  const box = (
    <TextField
      className="signage-secs"
      type="number"
      isInvalid={bad}
      aria-label={kind === 'image' ? undefined : say('signage.seconds')}
      value={text}
      onChange={(next) => {
        setText(next)
        onChange(typedEntry(entry, next, kind))
      }}
    >
      {kind === 'image' && <Label>{say('signage.seconds')}</Label>}
      <Input variant="secondary" inputMode="numeric" min={MIN_SECONDS} max={MAX_SECONDS} placeholder={kind === 'image' ? '10' : undefined} />
      <Description className="signage-range" data-bad={bad || undefined}>
        {say('signage.secondsRange', { min: MIN_SECONDS, max: MAX_SECONDS })}
      </Description>
    </TextField>
  )
  if (kind === 'image') return box
  const length = Math.round((media?.durationMs ?? 0) / 1000)
  return (
    <div className="signage-duration">
      <PickOne
        label={say('signage.duration')}
        value={toEnd ? 'end' : 'secs'}
        onChange={(v) => {
          if (v === 'end') {
            setText('')
            onChange({ ...kept, seconds: 0 })
            return
          }
          const n = length >= MIN_SECONDS && length <= MAX_SECONDS ? length : 10
          setText(String(n))
          onChange({ ...kept, seconds: n })
        }}
        options={[
          { value: 'end', label: say('signage.toEnd') },
          { value: 'secs', label: say('signage.seconds') },
        ]}
      />
      {!toEnd && box}
    </div>
  )
}

/** The selected item at the screen's shape, filled as the TV fills it. */
function Stage({ entry, media, shape, hold, onAdd }: { entry?: PlaylistEntry; media?: SignageMedia; shape: StageShape; hold: boolean; onAdd?: () => void }) {
  const { say } = useStaffText()
  const { width, height } = stageCanvas(shape)
  const film = useRef<HTMLVideoElement>(null)
  // Plays by itself unless less motion is asked for; held while Play runs over it.
  useEffect(() => {
    const v = film.current
    if (!v) return
    if (hold || reducedMotion()) v.pause()
    else void v.play().catch(() => undefined)
  }, [hold, media?.id])
  const { ref, scale } = useCanvasScale(width, height)
  const [natural, setNatural] = useState<{ width: number; height: number }>()
  useEffect(() => setNatural(undefined), [media?.id])
  const fit = objectFitOf(entry?.fit)
  const centred = fit === 'none' ? centreSize(natural, scale) : undefined
  const style: CSSProperties = centred ? { width: centred.width, height: centred.height, objectFit: 'fill' } : { width: '100%', height: '100%', objectFit: fit }
  const src = media ? api.assetURL(media.assetId) : ''
  return (
    <div className="signage-stage" data-shape={shape} ref={ref}>
      <div className="signage-stage__frame" style={{ width: width * scale, height: height * scale }}>
        {media?.kind === 'video' ? (
          <video
            key={media.id}
            ref={film}
            src={src}
            poster={media.posterAssetId ? api.assetURL(media.posterAssetId) : undefined}
            style={style}
            controls
            muted
            loop
            playsInline
            onLoadedMetadata={(e) => setNatural({ width: e.currentTarget.videoWidth, height: e.currentTarget.videoHeight })}
          />
        ) : media ? (
          <img key={media.id} src={src} alt="" style={style} onLoad={(e) => setNatural({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })} />
        ) : (
          <span className="signage-stage__empty">
            <span>{say('signage.noItems')}</span>
            {onAdd && (
              <Button variant="secondary" size="sm" onPress={onAdd}>
                <Icon name="plus" size={14} /> {say('common.add')}
              </Button>
            )}
          </span>
        )}
      </div>
    </div>
  )
}

/** The whole list as the hall TV plays it: the TV's own player, its timing and its fit. Esc closes. */
function PlayAll({ entries, library, shape, onClose }: { entries: PlaylistEntry[]; library: SignageMedia[]; shape: StageShape; onClose: () => void }) {
  const { say } = useStaffText()
  const { width, height } = stageCanvas(shape)
  const { ref, scale } = useCanvasScale(width, height)
  const close = useRef<HTMLButtonElement | null>(null)
  const box = useRef<HTMLDivElement>(null)
  const closing = useRef(onClose)
  closing.current = onClose
  const element: LayoutElement = { id: 'play', kind: 'media', x: 0, y: 0, w: width, h: height, z: 0, media: { items: playItems(entries, library) } }
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    close.current?.focus()
    const on = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closing.current()
      if (e.key === 'Tab' && box.current) {
        const items = [...box.current.querySelectorAll<HTMLElement>(FOCUSABLE)]
        const to = trapStep(items, document.activeElement instanceof HTMLElement ? document.activeElement : null, e.shiftKey)
        if (to) {
          e.preventDefault()
          to.focus()
        }
      }
    }
    window.addEventListener('keydown', on)
    return () => {
      window.removeEventListener('keydown', on)
      opener?.focus()
    }
  }, [])
  return (
    <div ref={box} className="signage-play" role="dialog" aria-modal="true" aria-label={say('signage.play')}>
      <Button ref={close} isIconOnly variant="ghost" className="signage-play__close" aria-label={say('signage.close')} onPress={onClose}>
        <Icon name="close" size={20} />
      </Button>
      <div className="layout-fit signage-play__fit" ref={ref}>
        <div className="layout-frame" style={{ width: width * scale, height: height * scale }}>
          <div className="layout-canvas signage-play__canvas" style={{ width, height, transform: `scale(${scale})` }}>
            <MediaPlayer element={element} assetURL={api.assetURL} />
          </div>
        </div>
      </div>
    </div>
  )
}

/** Library files to add, chosen together; files uploaded here are chosen already. */
function PickFiles({
  library,
  room,
  onUploaded,
  onPick,
  onClose,
}: {
  library: SignageMedia[]
  room: number
  onUploaded: (m: SignageMedia) => void
  onPick: (chosen: SignageMedia[]) => void
  onClose: () => void
}) {
  const { say, lang } = useStaffText()
  const [chosen, setChosen] = useState<string[]>([])
  const [uploads, setUploads] = useState<UploadRow[]>([])
  const [sending, setSending] = useState(false)
  const batch = useRef<AbortController | null>(null)
  useEffect(() => () => batch.current?.abort(), [])
  const byId = new Map(library.map((m) => [m.id, m]))
  const toggle = (id: string) => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : c.length < room ? [...c, id] : c))

  const uploadAll = async (files: File[]) => {
    if (sending || files.length === 0) return
    setSending(true)
    const stop = new AbortController()
    batch.current = stop
    try {
      await uploadEach(files, {
        signal: stop.signal,
        accept: (f) => accepts(LIBRARY_ACCEPT, f),
        send: async (f, progress, signal) => {
          const sent = await uploadToLibrary(f, progress, signal)
          onUploaded(sent.media)
          setChosen((c) => (c.includes(sent.media.id) || c.length >= room ? c : [...c, sent.media.id]))
          return { duplicate: sent.duplicate, name: sent.media.name }
        },
        explain: (err) => explain(err, lang, 'signage.uploadFailed'),
        notAccepted: (name) => say('common.fileNotAccepted', { name }),
        onChange: setUploads,
      })
    } finally {
      batch.current = null
      setSending(false)
    }
  }

  return (
    <SignageModal
      title={say('signage.chooseFile')}
      onClose={onClose}
      size="lg"
      footNote={<UploadButton accept={LIBRARY_ACCEPT} label={say('signage.upload')} disabled={sending} onFiles={(files) => void uploadAll(files)} />}
      foot={
        <>
          <Button variant="tertiary" onPress={onClose}>
            {say('common.cancel')}
          </Button>
          <Button variant="primary" isDisabled={chosen.length === 0} onPress={() => onPick(chosen.map((id) => byId.get(id)).filter((m): m is SignageMedia => m !== undefined))}>
            {chosen.length > 0 ? `${say('common.add')} · ${chosen.length}` : say('common.add')}
          </Button>
        </>
      }
    >
      {uploads.length > 0 && (
        <ul className="signage-uploads" aria-label={say('signage.upload')}>
          {uploads.map((row, i) => (
            <UploadLine key={`${i}:${row.name}`} row={row} />
          ))}
        </ul>
      )}
      {library.length === 0 ? (
        <Empty icon="upload" title={say('signage.library.empty')} />
      ) : (
        <ul className="signage-pick">
          {library.map((m) => (
            <li key={m.id}>
              <button type="button" className="signage-pick__item" aria-pressed={chosen.includes(m.id)} onClick={() => toggle(m.id)} {...filmHover}>
                <span className="signage-pick__face">
                  <Thumb media={m} className="signage-face" />
                  {chosen.includes(m.id) && (
                    <span className="signage-pick__mark">
                      <Icon name="check" size={14} />
                    </span>
                  )}
                </span>
                <span className="signage-pick__name">{m.name}</span>
                <span className="signage-pick__kind">{say(m.kind === 'video' ? 'signage.film' : 'signage.picture')}{filmLength(m) && ` · ${filmLength(m)}`}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </SignageModal>
  )
}
